import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

const NotificationContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const WS_BASE = API_URL.replace(/^http/, 'ws');
const RECONNECT_BASE_DELAY = 2000;
const RECONNECT_MAX_DELAY = 30000;
const POLL_INTERVAL = 30000;

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef(null);
  const pollTimer = useRef(null);

  const addNotification = useCallback((n) => {
    setNotifications(prev => {
      const updated = [n, ...prev].slice(0, 50);
      return updated;
    });
    if (!n.read) setUnreadCount(prev => prev + 1);
    // Show toast for new notifications
    const toastFn = n.severity === 'error' ? toast.error
      : n.severity === 'warning' ? toast.warning
      : n.severity === 'success' ? toast.success
      : toast.info;
    toastFn(n.title || 'Notification', { description: n.message, duration: 5000 });
  }, []);

  const markRead = useCallback((notificationId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'mark_read', notification_id: notificationId }));
    }
    setNotifications(prev => prev.map(n =>
      n.timestamp === notificationId ? { ...n, read: true } : n
    ));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAllRead = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'mark_all_read' }));
    } else {
      fetch(`${API_URL}/api/notifications/mark-read?user_id=demo-user`, { method: 'POST' }).catch(() => {});
    }
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  // Fallback REST polling
  const pollNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications?user_id=demo-user`);
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch {
      // Silent fail for polling
    }
  }, []);

  const connectWs = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      // Prefer subprotocol bearer auth; fallback to query param
      const ws = new WebSocket(
        `${WS_BASE}/api/ws/notifications?token=demo-token`,
        ['bearer.demo-token']
      );

      ws.onopen = () => {
        setConnected(true);
        reconnectAttempt.current = 0;
        // Stop polling when WS connected
        if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'ping') {
            ws.send(JSON.stringify({ type: 'pong' }));
            return;
          }
          if (data.type === 'replay' && data.notifications) {
            setNotifications(data.notifications);
            const unread = data.notifications.filter(n => !n.read).length;
            setUnreadCount(unread);
            return;
          }
          if (data.type === 'notification') {
            addNotification(data);
            return;
          }
          // 'connected' type — ignore silently
        } catch {
          // Malformed message
        }
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        // Start fallback polling
        if (!pollTimer.current) {
          pollTimer.current = setInterval(pollNotifications, POLL_INTERVAL);
        }
        // Reconnect with exponential backoff
        const delay = Math.min(RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttempt.current), RECONNECT_MAX_DELAY);
        reconnectAttempt.current += 1;
        reconnectTimer.current = setTimeout(connectWs, delay);
      };

      ws.onerror = () => {
        // onclose will fire after onerror
      };

      wsRef.current = ws;
    } catch {
      // WebSocket constructor failed — use polling
      if (!pollTimer.current) {
        pollTimer.current = setInterval(pollNotifications, POLL_INTERVAL);
      }
    }
  }, [addNotification, pollNotifications]);

  useEffect(() => {
    // Initial load via REST
    pollNotifications();
    // Connect WebSocket
    connectWs();

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [connectWs, pollNotifications]);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, connected, markRead, markAllRead }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
